import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Modal, tokens } from '@hotjar/hotsauce';
import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { closedModal, loadedModal, trackClick } from '@hotjar/webapp/tracking/common';
import { useOwnedAccount } from '@hotjar/account';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { ChurnDeflection } from './components/ChurnDeflection';
import { PlanChangeConfirmationSummary } from './components/PlanChangeConfirmationSummary';
import { MODAL_NAME } from './types';

const S = {
  Modal: styled(Modal)<{ responsive: boolean }>`
    ${({ responsive }) =>
      responsive &&
      css`
        width: calc(100% - calc(${tokens.spacer.x8} + ${tokens.spacer.x8}));
        max-width: 1200px;
        min-width: 800px;
      `};
  `,
};

interface Props {
  onCancel(error?: Error): void;
  onChange(): void;
  opened: boolean;
  upgradeCount: number;
  downgradeCount: number;
  downgradeHighestPlan?: FeatureGroup;
  payNowPrice?: PriceSummary;
  payLaterPrice?: PriceSummary;
  isLoading?: boolean;
}

export function PlanChangeConfirmation({
  onCancel,
  onChange,
  opened,
  upgradeCount,
  downgradeCount,
  downgradeHighestPlan,
  payNowPrice,
  payLaterPrice,
  isLoading,
}: Props): JSX.Element {
  const [currentModal, setCurrentModal] = useState<MODAL_NAME>();
  const { t: tBilling } = useTranslationWithPrefix('billing:plan-change-confirmation');
  const { data: account } = useOwnedAccount();
  const isChurning = payLaterPrice?.total?.eq('0');

  const handleOnCurrentModalChange = useCallback(
    (modalName: MODAL_NAME) => {
      setCurrentModal(modalName);
      loadedModal(modalName, extractAccountGroupingProps(account), {
        modal_site_count: upgradeCount + downgradeCount,
        modal_site_downgrades_count: downgradeCount,
        modal_site_upgrades_count: upgradeCount,
        ...(modalName === MODAL_NAME.churnDeflection && { modal_version: downgradeHighestPlan }),
      });
    },
    [account, upgradeCount, downgradeCount, downgradeHighestPlan],
  );

  useEffectOnceWithDependencies(
    () => {
      handleOnCurrentModalChange(isChurning ? MODAL_NAME.churnDeflection : MODAL_NAME.planChange);
    },
    [account],
    () => account !== undefined,
  );

  const handleOnChange = (clickName: string, submitChange = true) => {
    trackClick({
      name: clickName,
      uiPosition: `${currentModal} modal`,
      groupData: extractAccountGroupingProps(account),
    });
    if (submitChange) {
      onChange();
    }
  };

  const handleOnCancel = () => {
    closedModal(currentModal as MODAL_NAME, extractAccountGroupingProps(account));
    onCancel(new Error('Cancelled'));
  };

  return (
    <S.Modal
      isOpen={opened}
      size="m"
      contentLabel={tBilling('title')}
      onClose={handleOnCancel}
      responsive={currentModal === MODAL_NAME.churnDeflection}
    >
      <>
        {account && currentModal === MODAL_NAME.churnDeflection && (
          <ChurnDeflection
            highestPlan={downgradeHighestPlan}
            onSubmit={() => {
              handleOnChange('Continue to downgrade', false);
              handleOnCurrentModalChange(MODAL_NAME.churnSurvey);
            }}
            onCancel={handleOnCancel}
          />
        )}
        {account &&
          (currentModal === MODAL_NAME.churnSurvey || currentModal === MODAL_NAME.planChange) && (
            <PlanChangeConfirmationSummary
              onChange={handleOnChange}
              onCancel={handleOnCancel}
              upgradeCount={upgradeCount}
              downgradeCount={downgradeCount}
              payNowPrice={payNowPrice}
              payLaterPrice={payLaterPrice}
              isLoading={isLoading}
              isChurning={isChurning}
            />
          )}
      </>
    </S.Modal>
  );
}

function GlobalPlanChangeConfirmation(props: Props & GlobalModalProps<void>): JSX.Element {
  const { onSubmit, onChange, ...rest } = props;

  function handleChange() {
    onChange();
    onSubmit();
  }

  return <PlanChangeConfirmation {...rest} onChange={handleChange} />;
}

export const openPlanChangeConfirmationModal = createGlobalModal<Props, void, Error>(
  GlobalPlanChangeConfirmation,
);
